import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  User,
  UserCreate,
  UserGetFiltersResponse,
  UserGetResponse,
  UserUpdate,
} from '../types/user.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';
import { useActiveTenant } from '../../../../../states/active-tenant.atom';

const useUserBasePath = () => {
  const { activeTenant } = useActiveTenant();
  return useMemo(() => {
    return `/v1/admin/tenants/${activeTenant?.id}/users`;
  }, [activeTenant]);
};

export const useUserHooks = () => {
  const userBasePath = useUserBasePath();
  const { activeTenant } = useActiveTenant();
  return createApiHooks<User, UserGetResponse, UserCreate, UserUpdate>({
    basePath: userBasePath,
    queryKey: ['Users', 'Tenant', activeTenant!.id],
    apiClient,
  });
};

export const useUserGetFilters = (queryOptions?: any) => {
  const userBasePath = useUserBasePath();
  return useGenericGet<UserGetFiltersResponse>(
    `${userBasePath}/filters`,
    ['Users', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the user hooks
export const useUserApi = () => {
  const userHooks = useUserHooks();

  return {
    useGet: userHooks.useGet,
    useCreate: userHooks.useCreate,
    useUpdate: userHooks.useUpdate,
    useDelete: userHooks.useDelete,
    useList: (params: UseListParams) => userHooks.useList(params),
  };
};

// Export individual hooks for backward compatibility
export const useUserGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useUserApi();
  return useGet(id, queryOptions);
};

export const useUserCreate = (mutationOptions?: any) => {
  const { useCreate } = useUserApi();
  return useCreate(mutationOptions);
};

export const useUserUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useUserApi();
  return useUpdate(mutationOptions);
};

export const useUserDelete = (mutationOptions?: any) => {
  const { useDelete } = useUserApi();
  return useDelete(mutationOptions);
};

export const useUserList = (params: UseListParams) => {
  const { useList } = useUserApi();
  return useList(params);
};
